type ProductionPlaceMapTilerTileProvider = {
  attribution: string;
  key: string;
  label: string;
  labelKey: string;
  styleId: string;
};

function getRequiredEnv(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];

  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Missing required env variable: ${name}`);
  }

  return value;
}

function getRequiredNumberEnv(name: keyof ImportMetaEnv): number {
  const value = Number(getRequiredEnv(name));

  if (Number.isNaN(value)) {
    throw new Error(`Invalid numeric env variable: ${name}`);
  }

  return value;
}

export const PRODUCTION_PLACE_MAP_DEFAULT_LAT = getRequiredNumberEnv(
  'VITE_PRODUCTION_PLACE_MAP_DEFAULT_LAT',
);

export const PRODUCTION_PLACE_MAP_DEFAULT_LNG = getRequiredNumberEnv(
  'VITE_PRODUCTION_PLACE_MAP_DEFAULT_LNG',
);

export const PRODUCTION_PLACE_MAP_DEFAULT_ZOOM = getRequiredNumberEnv(
  'VITE_PRODUCTION_PLACE_MAP_DEFAULT_ZOOM',
);

export const PRODUCTION_PLACE_MAPTILER_API_KEY = getRequiredEnv(
  'VITE_PRODUCTION_PLACE_MAPTILER_KEY',
);

export const PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS = [
  {
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
    key: 'topo',
    label: 'Topo',
    labelKey:
      'materialCommodityOrigins.cards.productionPlace.mapTilerStyles.topo',
    styleId: 'topo-v4',
  },
  {
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
    key: 'outdoor',
    label: 'Outdoor',
    labelKey:
      'materialCommodityOrigins.cards.productionPlace.mapTilerStyles.outdoor',
    styleId: 'outdoor-v4',
  },
  {
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
    key: 'satelliteHybrid',
    label: 'Satellite hybrid',
    labelKey:
      'materialCommodityOrigins.cards.productionPlace.mapTilerStyles.satelliteHybrid',
    styleId: 'hybrid-v4',
  },
  {
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
    key: 'landscape',
    label: 'Landscape',
    labelKey:
      'materialCommodityOrigins.cards.productionPlace.mapTilerStyles.landscape',
    styleId: 'landscape-v4',
  },
  {
    attribution: '&copy; MapTiler &copy; OpenStreetMap contributors',
    key: 'basic',
    label: 'Base',
    labelKey:
      'materialCommodityOrigins.cards.productionPlace.mapTilerStyles.basic',
    styleId: 'base-v4',
  },
] as const satisfies readonly ProductionPlaceMapTilerTileProvider[];

export type ProductionPlaceMapTilerTileProviderKey =
  (typeof PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS)[number]['key'];

export const DEFAULT_PRODUCTION_PLACE_MAPTILER_TILE_PROVIDER_KEY =
  PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS[0].key;

export function isProductionPlaceMapTilerTileProviderKey(
  value: string,
): value is ProductionPlaceMapTilerTileProviderKey {
  return PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS.some(
    ({ key }) => key === value,
  );
}

export function getProductionPlaceMapTilerTileProvider(
  tileProviderKey: ProductionPlaceMapTilerTileProviderKey,
) {
  const tileProvider = PRODUCTION_PLACE_MAPTILER_TILE_PROVIDERS.find(
    ({ key }) => key === tileProviderKey,
  );

  if (!tileProvider) {
    throw new Error(`Unknown MapTiler tile provider key: ${tileProviderKey}`);
  }

  return tileProvider;
}

export function getProductionPlaceMapTilerVectorStyleUrl(
  styleId: string,
): string {
  return `https://api.maptiler.com/maps/${styleId}/style.json?key=${PRODUCTION_PLACE_MAPTILER_API_KEY}`;
}
